import useIdentity from "@/features/ui/hooks/use-identity";
import { Helmet } from "react-helmet-async";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppListHeader from "@/features/ui/app-list-header";
import AppSeasonMultiSelect from "@/features/line-sheet-sets/app-season-multi-select";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import { toTitle } from "@/utils/case";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppButton from "@/features/line-sheet-sets/app-button";
import { Box, Flex } from "@chakra-ui/react";
import AppPagination from "@/features/line-sheet-sets/app-pagination";
import { DEFAULT_LIST_INVOICES_PAGE_SIZE } from "@/features/invoices/hooks/use-list-invoices";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { useEffect, useMemo } from "react";
import {
  DEFAULT_LIST_CREDIT_INVOICES_PAGE_SIZE,
  ListDepositInvoicesRequest,
} from "@/features/invoices/deposit-invoices/hooks/use-list-deposit-invoices";
import useListDenseDepositInvoiceInvoices from "@/features/invoices/deposit-invoices/hooks/use-list-dense-deposit-invoices";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import DepositInvoiceGrid from "@/features/invoices/deposit-invoices/deposit-invoice-grid";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";

export interface PersistedDepositInvoiceListPageQuery
  extends Partial<ListDepositInvoicesRequest> {}

export default function DepositInvoiceListPage() {
  const identity = useIdentity();
  const { error: showError } = useAppToasts();
  const [query, setQuery] =
    useTypedSearchParams<PersistedDepositInvoiceListPageQuery>();
  const { tTitle } = useI18nHelper();
  const { t } = useI18nHelper();
  const { navigate } = useRouterHelper();

  const listDepositInvoicesKey = useMemo(() => {
    if (identity?.company) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          ...query,
          pageNumber: query.pageNumber || 1,
          pageSize: DEFAULT_LIST_CREDIT_INVOICES_PAGE_SIZE,
        },
      };
    }
  }, [identity, query]);

  const {
    data: listDenseDepositInvoicesData,
    error: listDenseDepositInvoicesError,
    isLoading: isListDenseDepositInvoiceInvoicesLoading,
    mutate: mutateListDenseDepositInvoiceInvoices,
  } = useListDenseDepositInvoiceInvoices(
    listDepositInvoicesKey?.parameter,
    listDepositInvoicesKey?.request,
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    showError(listDenseDepositInvoicesError);
  }, [showError, listDenseDepositInvoicesError]);

  return (
    <>
      <Helmet>
        <title>{`Pathrade | ${tTitle("deposit_invoices")}`}</title>
        <link
          rel="canonical"
          href={getCanonicalUrl()}
        />
        <meta
          property="og:title"
          content={`Pathrade | ${tTitle("deposit_invoices")}`}
        />
        <meta
          property="og:url"
          content={getCanonicalUrl()}
        />
      </Helmet>
      <Flex
        height={"100%"}
        flexDirection={"column"}
        gap={"12px"}
        paddingBottom={"16px"}
      >
        <AppListHeader
          filters={[
            <AppSeasonMultiSelect
              key={"season"}
              value={query.season__in}
              width={"120px"}
              onSelect={(value) =>
                setQuery((prev) => {
                  return {
                    ...prev,
                    season__in: value,
                    pageNumber: 1,
                  };
                })
              }
            />,
            // <AppRemittanceStatusSelector
            //   key={"AgencyInvoiceListFilter_RemittanceStatus"}
            //   value={query.remittanceStatus__in?.[0]}
            //   onSelect={(v) => patchQuery("remittanceStatus__in", [v])}
            // />,
            <AppCompanySelect
              key={"AgencyDepositInvoiceListFilter_OrderedBy"}
              name={toTitle(t("ordered_by"))}
              types={["BUYER"]}
              width={"120px"}
              id={query["issuedToCompany.id__eq"]}
              onSelect={(value) =>
                setQuery((prev) => {
                  return {
                    ...prev,
                    "issuedToCompany.id__eq": value?.id,
                    pageNumber: 1,
                  };
                })
              }
            />,
            <AppCompanySelect
              key={"AgencyDepositInvoiceListFilter_IssuedBy"}
              name={toTitle(t("issued_by"))}
              types={["BOUTIQUE"]}
              width={"120px"}
              id={query["issuedByCompany.id__eq"]}
              onSelect={(value) =>
                setQuery((prev) => {
                  return {
                    ...prev,
                    "issuedByCompany.id__eq": value?.id,
                    pageNumber: 1,
                  };
                })
              }
            />,
            <AppDatePicker
              key={"AgencyDepositInvoiceListFilter_IssuedOn"}
              name={toTitle(t("issued_on"))}
              value={query.issuedOn__eq}
              showTimeInput={false}
              width={"120px"}
              onChange={(value) =>
                setQuery((prev) => {
                  return {
                    ...prev,
                    issuedOn__eq: value,
                    pageNumber: 1,
                  };
                })
              }
            />,
            <AppButton
              key={"AgencyDepositInvoiceListFilter_ResetFilter"}
              variant={"normal"}
              width={"120px"}
              size={"medium"}
              onClick={() => {
                setQuery((prev) => {
                  return {
                    pageNumber: 1,
                  };
                });
              }}
            >
              {toTitle(t("reset_filter"))}
            </AppButton>,
          ]}
          actions={[
            <AppButton
              key={"AgencyDepositInvoiceListAction_UploadInvoices"}
              variant={"primary"}
              size={"medium"}
              onClick={() => {
                navigate("/deposit-invoices/upload");
              }}
            >
              {toTitle(
                t("upload_deposit_invoices", { ns: "deposit_invoices" })
              )}
            </AppButton>,
          ]}
        />
        <Box
          flexGrow={1}
          overflow={"auto"}
          style={{
            scrollbarGutter: "stable",
          }}
        >
          <DepositInvoiceGrid
            resources={listDenseDepositInvoicesData?.depositInvoices}
            mutateResources={mutateListDenseDepositInvoiceInvoices}
          />
        </Box>
        <Flex flexDirection={"row"} justifyContent={"center"}>
          <AppPagination
            capacity={10}
            pageSize={DEFAULT_LIST_INVOICES_PAGE_SIZE}
            totalCount={listDenseDepositInvoicesData?.totalCount}
            pageNumber={query.pageNumber}
            setPageNumber={(value) =>
              setQuery((prev) => {
                return {
                  ...prev,
                  pageNumber: value,
                };
              })
            }
          />
        </Flex>
      </Flex>
    </>
  );
}
